import { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { PageTitle } from 'shared/components/Fonts';
import { useParams, useNavigate } from 'react-router-dom';
import { useCoreService } from 'core/services/useCoreService';
import { useNotifications } from 'shared/notifications';
import { useDataFetcher } from 'shared/http';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { Button, IconName } from '@orbita-ui/core';
import { CrearCentroCostoRequest } from 'core/cuenta/models/requests/CrearCentroCostoRequest';
import { CostCentreForm } from 'core/centrocosto/components/CostCentreForm/CostCentreForm';
import { COST_CENTRE_LIST_PATH, COST_CENTRE_MOVE_PATH } from 'core/centrocosto/routes';
import { CustomBreadcrumb } from 'shared/components/CustomBreadcrumb';
import {
  DEFAULT_SAVE_COST_CENTRE_API_ERRORS,
  SAVE_COST_CENTRE_ERROR_MESSAGES,
} from 'core/centrocosto/constants/SaveCostCentreMessages';
import { useImpersonationGuard } from 'auth/hooks/useImpersonationGuard';

export function CostCentreEditPage() {
  const breadcrumbData: {
    icon: IconName;
    items: string[];
    lastItem: string;
  } = {
    icon: 'BankIcon',
    items: ['Organización'],
    lastItem: 'Modificar centro de costos',
  };

  const { id } = useParams();
  const { addNotification } = useNotifications();
  const [centroCostoResponse, setFetchCentroCosto] = useDataFetcher<CentroCosto>();
  const coreService = useCoreService();
  const navigate = useNavigate();
  const { guardWriteAction } = useImpersonationGuard();
  const [isLoading, setIsloading] = useState(false);
  const [apiErrors, setApiErrors] = useState(DEFAULT_SAVE_COST_CENTRE_API_ERRORS);

  useEffect(() => {
    setFetchCentroCosto(() => coreService.getCostCentre(id));
  }, [coreService, setFetchCentroCosto, id]);

  const onSubmit = async (formData) => {
    if (!guardWriteAction()) {
      return;
    }
    setIsloading(true);

    try {
      const request = new CrearCentroCostoRequest({
        id: centroCostoResponse.data.id,
        denominacion: formData.denominacion,
      });

      await coreService.updateCostCentre(request);
      addNotification('Centro de costo actualizado', 'success');
      navigate(COST_CENTRE_LIST_PATH);
    } catch (error) {
      const errorKey = error.apiError.message;

      if (errorKey in DEFAULT_SAVE_COST_CENTRE_API_ERRORS) {
        setApiErrors({ ...DEFAULT_SAVE_COST_CENTRE_API_ERRORS, [errorKey]: true });
      } else {
        // Error no manejado
        addNotification(SAVE_COST_CENTRE_ERROR_MESSAGES.default, 'error');
        setApiErrors(DEFAULT_SAVE_COST_CENTRE_API_ERRORS);
      }
    } finally {
      setIsloading(false);
    }
  };

  return (
    <Container fluid>
      <CustomBreadcrumb
        icon={breadcrumbData.icon}
        items={breadcrumbData.items}
        lastItem={breadcrumbData.lastItem}
      />
      <Row className="mb-3 d-flex justify-content-between align-items-center">
        <Col>
          <PageTitle>Editar Centro de Costos</PageTitle>
        </Col>
        <Col className="text-end">
          <Button
            color="primary"
            icon="SortHorizontalIcon"
            type="button"
            onClick={() =>
              navigate(COST_CENTRE_MOVE_PATH.replace(':id', String(centroCostoResponse.data.id)))
            }
          >
            Mover Centro de Costos
          </Button>
        </Col>
      </Row>
      <Row>
        <CostCentreForm
          onSubmit={onSubmit}
          centroCosto={centroCostoResponse.data}
          submitLabel="Modificar"
          fields={{ centroCosto: false, cuenta: false, denominacion: true }}
          isLoading={centroCostoResponse.isLoading || isLoading}
          apiErrors={apiErrors}
        />
      </Row>
    </Container>
  );
}
